import { useMemo } from 'react';
import type { CustomArea } from '../../types/domain';
import type { ApiDataFilter } from '../../infrastructure/dataApi/client';
import { useCustomAreas } from './useCustomAreas';
import { useDataFilter } from './useDataFilter';
import { useCustomAreaEdition } from './useCustomAreaEdition';

type UseApiDataFilter = {
  apiFilter: ApiDataFilter;
};

const useApiDataFilter = (): UseApiDataFilter => {
  const { dataFilter } = useDataFilter();
  const { customAreas } = useCustomAreas();
  const { editingCustomArea, initialCustomArea } = useCustomAreaEdition();

  // 編集中のカスタムエリアの代わりに編集前のものを使うことで、編集中に何度もデータの再取得が行われないようにする
  const confirmedCustomAreas = useMemo((): CustomArea[] => {
    if (!editingCustomArea || !initialCustomArea) {
      return customAreas;
    }
    return customAreas.map((customArea: CustomArea): CustomArea => {
      return customArea.key === editingCustomArea.key
        ? initialCustomArea
        : customArea;
    });
  }, [customAreas, editingCustomArea, initialCustomArea]);

  const apiFilter = useMemo((): ApiDataFilter => {
    // カスタムエリアのキーを座標に解決する
    const getPolygonCoordinates = (
      customAreaKeys: string[]
    ): [number, number][][] | undefined =>
      customAreaKeys
        .map((key: string) => {
          return confirmedCustomAreas.find(
            (customArea: CustomArea): boolean => {
              return customArea.key === key;
            }
          )?.geometry.coordinates;
        })
        .filter((coordinates): coordinates is [number, number][] => {
          return !!coordinates;
        });
    const convertToUndefinedIfEmpty = <T>(
      array: T[] | undefined
    ): T[] | undefined => {
      return array && array.length > 0 ? array : undefined;
    };

    return {
      start: {
        area: convertToUndefinedIfEmpty(dataFilter.start.areaKeyCodes),
        polygon: convertToUndefinedIfEmpty(
          getPolygonCoordinates(dataFilter.start.customAreaKeys)
        ),
      },
      end: {
        area: convertToUndefinedIfEmpty(dataFilter.end.areaKeyCodes),
        polygon: convertToUndefinedIfEmpty(
          getPolygonCoordinates(dataFilter.end.customAreaKeys)
        ),
      },
      pass: {
        area: convertToUndefinedIfEmpty(dataFilter.pass.areaKeyCodes),
        link: convertToUndefinedIfEmpty(dataFilter.pass.wayIds),
        polygon: convertToUndefinedIfEmpty(
          getPolygonCoordinates(dataFilter.pass.customAreaKeys)
        ),
        relation: dataFilter.passRelation ?? undefined,
      },
      home: {
        area: convertToUndefinedIfEmpty(dataFilter.home.areaKeyCodes),
        polygon: convertToUndefinedIfEmpty(
          getPolygonCoordinates(dataFilter.home.customAreaKeys)
        ),
      },
      work: {
        area: convertToUndefinedIfEmpty(dataFilter.work.areaKeyCodes),
        polygon: convertToUndefinedIfEmpty(
          getPolygonCoordinates(dataFilter.work.customAreaKeys)
        ),
      },
      visit: {
        area: convertToUndefinedIfEmpty(dataFilter.visit.areaKeyCodes),
        polygon: convertToUndefinedIfEmpty(
          getPolygonCoordinates(dataFilter.visit.customAreaKeys)
        ),
      },
      end_activity_type: convertToUndefinedIfEmpty(dataFilter.endActivityTypes),
      longest_distance_mode: convertToUndefinedIfEmpty(
        dataFilter.longestDistanceModes
      ),
      traveled_time: dataFilter.traveledTime ?? undefined,
      traveled_distance: dataFilter.traveledDistance ?? undefined,
      activity_time: dataFilter.activityTime ?? undefined,
      dep_time: dataFilter.depTime ?? undefined,
      arr_time: dataFilter.arrTime ?? undefined,
      passage_time: dataFilter.passageTime ?? undefined,
      age: convertToUndefinedIfEmpty(dataFilter.ageGroups.map(Number)),
      gender: convertToUndefinedIfEmpty(dataFilter.genderIds.map(Number)),
      nationality: convertToUndefinedIfEmpty(dataFilter.nationalities),
      staying_time: dataFilter.stayingTime ?? undefined,
      stopBy: dataFilter.stopBy ?? undefined,
    };
  }, [dataFilter, confirmedCustomAreas]);

  return { apiFilter };
};

export { useApiDataFilter };
